import React, { useState } from "react";

import axios from "axios";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import Button from "../../components/Button/Button";
import FloatingLabelInput from "../../components/FloatingLabelInput/FloatingLabelInput";
import Modal from "../../components/modal/Modal";
import Loader from "../../components/loader/loader";
import AddVoucherPayment from "../../components/add-voucher-payment/add-voucher-payment";
import AddOneVoucherPayment from "../../components/add-one-voucher-payment/add-one-voucher-payment";
import AccessoryVoucherPayment from "../../components/accessory-voucher-payment/accessory-voucher-payment";
import { backendURL } from "../../utils/backed-url";
import useUserRoleStore from "../../store/user-role";
import { homeForRole } from "../../utils/routes";
import { rememberPaymentReturn } from "../../utils/payment-return";
import { formatPrice } from "../../utils/format-price";

// Clients do not know their email address. The backend creates their account
// as "<client id>@zonkeenergy.com" with their SA ID as the password, so we
// build the email here and only ever ask for the two things they do know.
const CLIENT_EMAIL_DOMAIN = "zonkeenergy.com";

const steps = {
  START: "start",
  ACCESSORY: "accessory",
  METHOD: "method",
  AUTH: "auth",
};

const intents = {
  TOP_UP: "top-up",
  ACCESSORY: "accessory",
  LOGIN: "login",
};

const Landing = () => {
  const navigate = useNavigate();
  const { setRole } = useUserRoleStore();

  const [step, setStep] = useState(steps.START);
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [payByVoucherModal, setPayByVoucherModal] = useState(false);
  const [payByOneVoucherModal, setPayByOneVoucherModal] = useState(false);
  const [accessories, setAccessories] = useState([]);
  const [accessoriesError, setAccessoriesError] = useState("");
  const [selectedAccessory, setSelectedAccessory] = useState(null);

  const {
    handleSubmit,
    register,
    trigger,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      clientNumber: "",
      saId: "",
    },
  });

  const onStart = async (nextIntent) => {
    const isValid = await trigger("clientNumber");
    if (!isValid) {
      return;
    }

    setClientId(parseInt(getValues("clientNumber"), 10));
    setIntent(nextIntent);

    if (nextIntent === intents.ACCESSORY) {
      setSelectedAccessory(null);
      setStep(steps.ACCESSORY);
      loadAccessories();
      return;
    }
    setStep(nextIntent === intents.TOP_UP ? steps.METHOD : steps.AUTH);
  };

  // Names and prices are edited in the admin, so they are fetched fresh each
  // time rather than kept in the bundle.
  const loadAccessories = async () => {
    setLoading(true);
    setAccessoriesError("");
    try {
      const { data } = await axios.get(`${backendURL}/v1/api/accessories/`);
      setAccessories(data);
    } catch {
      setAccessoriesError(
        "We could not load the accessories, please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const onBack = () => {
    clearErrors("saId");
    if (step === steps.AUTH && intent !== intents.LOGIN) {
      setStep(steps.METHOD);
      return;
    }
    if (step === steps.METHOD && intent === intents.ACCESSORY) {
      setStep(steps.ACCESSORY);
      return;
    }
    setStep(steps.START);
  };

  // Agents top up one client after another, so a finished payment goes back
  // to an empty client number rather than this client's payment methods.
  const onVoucherPaid = () => {
    setPayByVoucherModal(false);
    setPayByOneVoucherModal(false);
    setClientId(null);
    setIntent(null);
    setSelectedAccessory(null);
    reset();
    setStep(steps.START);
  };

  const onLogin = async ({ clientNumber, saId }) => {
    setLoading(true);

    if (intent === intents.ACCESSORY) {
      await payAccessoryWithOzow(saId);
      return;
    }

    try {
      const { data } = await axios.post(`${backendURL}/users/api/login`, {
        email: `${clientNumber}@${CLIENT_EMAIL_DOMAIN}`,
        password: saId,
      });

      localStorage.setItem("token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      localStorage.setItem("role", data.role);
      setRole(data.role);

      if (intent === intents.TOP_UP) {
        navigate("/top-up", { state: { openPayByBank: true } });
      } else {
        navigate(homeForRole(data.role));
      }
    } catch (e) {
      setError("saId", {
        type: "manual",
        message:
          e?.response?.status === 401
            ? "We could not match that client number and ID number."
            : "Something went wrong, please try again.",
      });
      setLoading(false);
    }
  };

  // The backend charges the accessory's own price, whatever amount is sent, so
  // the client cannot pay more or less than it costs. It also checks the ID
  // number against the client number, so there is no need to log in first.
  const payAccessoryWithOzow = async (saId) => {
    const reference = Date.now();
    try {
      const { data } = await axios.post(
        `${backendURL}/v1/api/initiate-payment/`,
        {
          amount: selectedAccessory.price,
          transaction_ref: `TR-${reference}`,
          bank_ref: `BR-${reference}`,
          client_id: clientId,
          accessory_id: selectedAccessory.id,
          sa_id: saId,
        }
      );
      rememberPaymentReturn(`TR-${reference}`, {
        returnTo: "/",
        accessoryName: selectedAccessory.name,
      });
      window.location.assign(data.payment_url);
    } catch (e) {
      setError("saId", {
        type: "manual",
        message:
          e?.response?.data?.error ||
          "We could not start the payment, please try again.",
      });
      setLoading(false);
    }
  };

  // Ozow needs the client logged in before it can take a payment. The vouchers
  // do not, so they open their modal here rather than adding a step.
  // 1Voucher cannot be checked for its value before it is redeemed, so it is
  // not offered for accessories, which must be paid for at exactly their price.
  const allMethods = [
    {
      key: "ozow",
      logo: "/images/ozow.png",
      label: "Pay with Ozow",
      onSelect: () => setStep(steps.AUTH),
    },
    {
      key: "ott",
      logo: "/images/ott.png",
      label: "Pay with an OTT voucher",
      onSelect: () => setPayByVoucherModal(true),
    },
    {
      key: "flash",
      logo: "/images/flash.png",
      label: "Pay with a 1Voucher",
      onSelect: () => setPayByOneVoucherModal(true),
    },
  ];
  const methods =
    intent === intents.ACCESSORY
      ? allMethods.filter(({ key }) => key !== "flash")
      : allMethods;

  const subtitle = {
    [steps.START]: "Enter your client number to get started",
    [steps.ACCESSORY]: "What would you like to buy?",
    [steps.METHOD]:
      intent === intents.ACCESSORY && selectedAccessory
        ? `How would you like to pay for the ${selectedAccessory.name} (${formatPrice(selectedAccessory.price)})?`
        : "How would you like to pay?",
    [steps.AUTH]: "Enter your ID number to continue",
  }[step];

  return (
    <div className="signup-container">
      <Modal
        modal={payByVoucherModal}
        onClose={() => setPayByVoucherModal(false)}
        modalSize="large"
      >
        {intent === intents.ACCESSORY ? (
          <AccessoryVoucherPayment
            onCancel={() => setPayByVoucherModal(false)}
            onSuccess={onVoucherPaid}
            clientId={clientId}
            accessory={selectedAccessory}
          />
        ) : (
          <AddVoucherPayment
            onCancel={() => setPayByVoucherModal(false)}
            onSuccess={onVoucherPaid}
            clientId={clientId}
          />
        )}
      </Modal>

      <Modal
        modal={payByOneVoucherModal}
        onClose={() => setPayByOneVoucherModal(false)}
        modalSize="large"
      >
        <AddOneVoucherPayment
          onCancel={() => setPayByOneVoucherModal(false)}
          onSuccess={onVoucherPaid}
          clientId={clientId}
        />
      </Modal>

      <div className="signup-card">
        <div className="signup__logo-wrapper">
          <img
            className="signup__logo"
            src={"/images/zonkelogo.jpg"}
            alt="logo"
          />
        </div>
        <div className="signup__title">Zonke Energy</div>
        <div className="signup__subtitle">{subtitle}</div>

        {step === steps.START && (
          <form
            className="landing__form"
            onSubmit={(event) => {
              event.preventDefault();
              onStart(intents.TOP_UP);
            }}
          >
            <div className="form-group">
              <FloatingLabelInput
                type={"text"}
                className={""}
                disabled={false}
                name={"clientNumber"}
                register={register}
                rules={{
                  required: "Client number is required",
                  pattern: {
                    value: /^[0-9]+$/,
                    message: "Client number must contain only numbers",
                  },
                }}
                placeholder={"Client Number"}
                errors={errors}
                autoComplete={"username"}
              />
            </div>

            <div className="landing__button-group">
              <Button
                type={"button"}
                onClick={() => onStart(intents.TOP_UP)}
                name={"Top Up"}
                className={"button--primary"}
              />
              <Button
                type={"button"}
                onClick={() => onStart(intents.ACCESSORY)}
                name={"Buy Accessories"}
                className={"button--primary"}
              />
              <Button
                type={"button"}
                onClick={() => onStart(intents.LOGIN)}
                name={"Login"}
                className={"button--primary"}
              />
            </div>
          </form>
        )}

        {step === steps.ACCESSORY && (
          <>
            {accessoriesError && (
              <div className="input-container__error">{accessoriesError}</div>
            )}
            {loading ? (
              <Loader sizeClass={"size--medium"} />
            ) : (
              <div
                className="landing__button-group landing__button-group--methods"
                role="radiogroup"
                aria-label="Accessories"
              >
                {accessories.map((accessory) => {
                  const selected = selectedAccessory?.id === accessory.id;
                  return (
                    <button
                      key={accessory.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`button landing__method-button landing__accessory${
                        selected ? " landing__accessory--selected" : ""
                      }`}
                      onClick={() => setSelectedAccessory(accessory)}
                    >
                      <span>{accessory.name}</span>
                      <span className="landing__accessory-price">
                        {formatPrice(accessory.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="landing__button-group">
              <Button
                type={"button"}
                onClick={() => setStep(steps.METHOD)}
                name={"Next"}
                className={"button--primary"}
                disabled={!selectedAccessory}
              />
            </div>
            <button type="button" className="landing__back" onClick={onBack}>
              Back
            </button>
          </>
        )}

        {step === steps.METHOD && (
          <>
            <div className="landing__button-group landing__button-group--methods">
              {methods.map(({ key, logo, label, onSelect }) => (
                <button
                  key={key}
                  type="button"
                  className="button landing__method-button"
                  onClick={onSelect}
                >
                  {/* The mark carries the brand name, so alt is what names
                      the button for anyone who cannot see it. */}
                  <img
                    className="landing__method-logo"
                    src={logo}
                    alt={label}
                  />
                </button>
              ))}
            </div>
            <button type="button" className="landing__back" onClick={onBack}>
              Back
            </button>
          </>
        )}

        {step === steps.AUTH && (
          <>
            <form className="landing__form" onSubmit={handleSubmit(onLogin)}>
              <div className="form-group">
                <FloatingLabelInput
                  type={"password"}
                  className={""}
                  disabled={loading}
                  name={"saId"}
                  register={register}
                  rules={{ required: "Your ID number is required" }}
                  placeholder={"ID Number"}
                  errors={errors}
                  autoComplete={"current-password"}
                />
              </div>

              <div className="landing__button-group">
                <Button
                  type={"submit"}
                  loading={loading}
                  name={
                    {
                      [intents.TOP_UP]: "Continue to Top Up",
                      [intents.ACCESSORY]: "Continue to Payment",
                    }[intent] || "Login"
                  }
                  className={"button--primary"}
                />
              </div>
            </form>
            <button
              type="button"
              className="landing__back"
              onClick={onBack}
              disabled={loading}
            >
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Landing;
